import moment, { Moment } from 'moment';
import * as Yup from 'yup';
import { Geometry } from '@turf/helpers';
import LayoutStrings from './layoutStrings';
import LayoutConstants from './layoutConstants';
import { AreaType } from './areaTypeConstants';
import isTankInfoIncomplete from '../Components/ProductsStep/helpers/isTankInfoIncomplete';

export interface Applicator {
  personId?: string;
  companyId?: string;
}

export interface PersonCompany {
  id: string;
  name: string;
  type?: string;
  licenseNumber?: string;
}

export interface PestOption {
  id: string;
  pestId: string;
  nameId: string;
  name: string;
  language: string;
}

export interface CropZone {
  id: string;
  name: string;
  fieldId: string;
  fieldName: string;
  isFieldArchived?: boolean;
  crop: {
    id: string;
    name: string;
  };
  cropSeasons: Array<{ id: string }>;
  propertyId: string;
  propertyName: string;
  isPropertyArchived?: boolean;
  area: number;
  geometry: Geometry;
  fieldGeometry?: any[]; // Array of GeoJSON Features from the parent field(s)
  appliedAcreage: number; // Editable, defaults to area
  coveragePercent: number; // Editable, defaults to 100
  // Optional properties for area type selection
  reportedArea?: number;
  boundaryArea?: number;
  selectedAreaType?: AreaType; // Track which area type is currently selected
}

export interface CropSeason {
  startDate: string;
  startDateEpoch: number;
  endDate: string;
  endDateEpoch: number;
  createdDate: string;
  createdDateEpoch: number;
  id: string;
  name: string;
  active: boolean;
  notes: string;
  cropzones: CropZone[];
  farms: string[];
  crops: {
    id: string;
    name: string;
  }[];
  totalArea: number;
}

export interface Product {
  id: string;
  productApplicationId?: string; // Unique ID for this product instance in the application
  name: string;
  manufacturer?: string;
  productType?: string;
  isInUse?: boolean;

  // values for the product
  quantityMode?: string;

  // totals
  ratePerAcre?: number | '';
  ratePerAreaUnit: string;

  totalProduct?: number | '';
  totalProductUnit: string;

  ratePerTank: number | '';
  ratePerTankUnit?: string;

  // prices
  priceValue?: number;
  priceUnit?: string;

  saveAsNewListPrice?: boolean;

  // check what is the difference between these values
  averagePricePerUnit?: number;
  averagePricePerUnitUnit?: string;
  totalCost?: number;

  // area
  totalArea?: number;
  totalAppliedAcreage: number;
  appliedAreaValue?: number;
  coveragePercent?: number;

  // other
  applicationMethod?: string;
  targetPests?: PestOption[];
  custom: boolean;
  registrationNumber?: string;
  registrationnumber?: string;

  // for calculations
  density: number;
  stdpackageunit: string;
  stdfactor?: number;
  stdunit: string;

  // for carrier
  isCarrier: boolean;
  isHidden: boolean;
}

export interface WeatherData {
  skyCondition: string;
  temperature: string;
  humidity: string;
  windSpeed: string;
  windDirection: string;
  soilMoisture: string;
  // Units from API
  temperatureUnit: string;
  humidityUnit: string;
  windSpeedUnit: string;
  // User selected units
  temperatureSelectedUnit: string;
  windSpeedSelectedUnit: string;
  manuallyModifiedFields: Array<
    keyof Omit<
      WeatherData,
      | 'manuallyModifiedFields'
      | 'temperatureSelectedUnit'
      | 'windSpeedSelectedUnit'
      | 'humidityUnit'
      | 'soilMoisture'
    >
  >;
}

export interface Equipment {
  id: string;
  name: string;
  type?: string;
}

export enum WeatherMode {
  AUTOMATIC = 'AUTOMATIC',
  MANUAL = 'MANUAL'
}

export enum RecordSourceType {
  WORK_ORDER = 'WORK_ORDER',
  APPLICATION = 'APPLICATION',
  INVOICE = 'INVOICE',
  RECOMMENDATION = 'RECOMMENDATION',
  PLANS = 'PLANS',
  INTEGRATIONS = 'INTEGRATIONS'
}

export enum IntegrationSourceType {
  FARMOBILE = 'FARMOBILE',
  TRIMBLE_AGRICULTURE = 'TRIMBLE_AGRICULTURE',
  AGFINITI = 'AGFINITI',
  CNHI = 'CNHI',
  JOHN_DEERE = 'JOHN_DEERE',
  OTHER = 'OTHER', // file upload
  CONNECT = 'CONNECT' // fallback
}

export interface RecordSource {
  recordSourceId: string;
  recordSourceName: string;
  recordSourceType: RecordSourceType;
  recordSourceAdditionalInfo?: string;
  integrationSourceType?: IntegrationSourceType;
  deleted?: boolean; // To indicate if the source record has been deleted
}

export interface FormValues {
  id?: string;
  // Step 1: Details
  applicationId: string;
  workType: string;
  timingEvent?: string;
  startDateTime: Moment;
  endDateTime?: Moment;
  durationHours?: number;
  durationMinutes?: number;
  authorizationDate: Moment;
  authorizedBy?: PersonCompany;
  applicationMethod?: string;
  equipments: Equipment[];
  tagIds: string[];
  applicators: Applicator[];
  notes?: string;
  // Step 2: Properties
  cropZones: CropZone[];
  totalArea?: number;
  totalCost?: number;
  totalAppliedAcreage: number;
  // Step 3: Products
  products: Product[];
  isTankInfoEnabled: boolean;
  tankSize?: number;
  tankCount?: number;
  overallRatePerAcre?: number;
  totalProduct?: number;
  isCarrierRowHidden: boolean;
  // Step 4: Weather (optional)
  weather?: {
    mode: WeatherMode;
    displayAutomaticWeatherModal?: boolean;
    start: WeatherData;
    end: WeatherData;
  };
  // Additional field to track the recorded by user
  recordSource?: RecordSource;
  isCarrier?: boolean; // To track if carrier product is added
  isHidden?: boolean; // To track if carrier product row is hidden
}

export const initialDetailsValues = {
  applicationId: '',
  workType: 'general',
  timingEvent: '',
  startDateTime: moment().set({
    hour: 8,
    minute: 0,
    second: 0,
    millisecond: 0
  }),
  endDateTime: moment().set({
    hour: 10,
    minute: 0,
    second: 0,
    millisecond: 0
  }),
  durationHours: 2,
  durationMinutes: 0,
  authorizationDate: moment(),
  authorizedBy: {
    id: '',
    name: '',
    type: ''
  },
  applicationMethod: '',
  equipments: [],
  tagIds: [],
  totalCost: 0,
  applicators: [{ personId: '', companyId: '' }],
  notes: ''
};

export const initialPropertiesValues = {
  cropZones: [],
  totalArea: 0,
  totalAppliedAcreage: 0
};

export const initialProductsValues = {
  products: [],
  isTankInfoEnabled: false,
  isCarrierRowHidden: false
};

export const initialWeatherValues = {
  weather: {
    mode: WeatherMode.AUTOMATIC,
    start: {
      skyCondition: '',
      temperature: '',
      humidity: '',
      windSpeed: '',
      windDirection: '',
      soilMoisture: '',
      temperatureUnit: '',
      humidityUnit: '',
      windSpeedUnit: '',
      soilMoistureUnit: '',
      temperatureSelectedUnit: '°F',
      windSpeedSelectedUnit: 'MPH',
      manuallyModifiedFields: []
    },
    end: {
      skyCondition: '',
      temperature: '',
      humidity: '',
      windSpeed: '',
      windDirection: '',
      soilMoisture: '',
      temperatureUnit: '',
      humidityUnit: '',
      windSpeedUnit: '',
      soilMoistureUnit: '',
      temperatureSelectedUnit: '°F',
      windSpeedSelectedUnit: 'MPH',
      manuallyModifiedFields: []
    }
  }
};

export const initialValues: FormValues = {
  ...initialDetailsValues,
  ...initialPropertiesValues,
  ...initialProductsValues,
  ...initialWeatherValues
};

// Reusable weather data validation schema
const weatherDataSchema = Yup.object().shape({
  skyCondition: Yup.string().nullable(),
  temperature: Yup.string().nullable(),
  temperatureUnit: Yup.string().nullable(),
  temperatureSelectedUnit: Yup.string().nullable(),
  humidity: Yup.string().nullable(),
  humidityUnit: Yup.string().nullable(),
  windSpeed: Yup.string().nullable(),
  windSpeedUnit: Yup.string().nullable(),
  windSpeedSelectedUnit: Yup.string().nullable(),
  windDirection: Yup.string().nullable(),
  soilMoisture: Yup.string().nullable(),
  soilMoistureUnit: Yup.string().nullable()
});

export const validationSchema = Yup.object().shape({
  applicationId: Yup.string().required('ID is required'),
  workType: Yup.string().required('Work Type is required'),
  startDateTime: Yup.date().required('Start Date & Time is required'),
  endDateTime: Yup.date().required('End Date & Time is required'),
  authorizationDate: Yup.date().required('Authorization Date is required'),
  tagIds: Yup.array().notRequired(),
  durationHours: Yup.number()
    .min(0, 'Duration hours cannot be negative')
    .required('Duration hours is required')
    .test(
      'duration-positive',
      LayoutStrings.errors.durationPositive,
      function durationHoursPositive(this: Yup.TestContext, value: number) {
        const { durationMinutes } = this?.parent as FormValues;
        return value + durationMinutes! > 0;
      }
    ),
  durationMinutes: Yup.number()
    .min(0)
    .max(59)
    .required('Duration minutes is required')
    .test(
      'duration-positive',
      LayoutStrings.errors.durationPositive,
      function durationMinutesPositive(this: Yup.TestContext, value: number) {
        const { durationHours } = this?.parent as FormValues;
        return durationHours! + value > 0;
      }
    ),
  notes: Yup.string().max(
    LayoutConstants.NOTES_MAX_CHARACTERS,
    `Notes cannot exceed ${LayoutConstants.NOTES_MAX_CHARACTERS.toLocaleString()} characters`
  ),
  cropZones: Yup.array().min(1, 'At least one crop zone is required'),
  products: Yup.array().min(1, 'At least one product is required'),
  isTankInfoEnabled: Yup.boolean().test(
    'tank-info',
    LayoutStrings.errors.tankInfoIncomplete,
    function tankInfoComplete(this: Yup.TestContext) {
      return !isTankInfoIncomplete(this.parent);
    }
  ),
  weather: Yup.object()
    .shape({
      start: weatherDataSchema,
      end: weatherDataSchema
    })
    .notRequired()
});

export default {
  initialValues,
  validationSchema
};
